import { useEffect, useState, useContext, useMemo, useRef } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
  Cell,
} from "recharts";
import BaseLayout from "../../layouts/BaseLayout/BaseLayout";
import { AnswersContext } from "../../store/AnswersContext/AnswersContext";
import ChartToolTip from "../../components/ChartToolTip/ChartToolTip";
import { calculateMarginBottom, prepareDayOfWeekData, prepareHourOfDayData, parseTimestamp } from "../../utils/chartUtils";
import UploadModal from "../../components/UploadModal/UploadModal";

export default function Profiling() {
  const { answerData, parseXlsxFile } = useContext(AnswersContext);
  const [chartsData, setChartsData] = useState([]);
  const [abandonmentStats, setAbandonmentStats] = useState(null);
  const [isModalOpen, setModalOpen] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null); // null = tutti i giorni

  const isEmpty = !Array.isArray(answerData) || answerData.length === 0;

  // Mantieni i dati precedenti per evitare flash durante l'aggiornamento (usando useRef per evitare re-render)
  const previousChartsDataRef = useRef([]);
  const previousDayOfWeekDataRef = useRef([]);
  const previousHourDataRef = useRef([]);

  // Funzione per formattare i numeri:
  // - 0 → "0"
  // - 1-99 → numero intero
  // - 100-999 → in migliaia approssimate (es. 0.5k, 0.1k)
  // - >= 1000 → in migliaia approssimate (es. 1.9k, 2.8k)
  const formatCountLabel = (value) => {
    if (typeof value !== 'number' || isNaN(value)) return value;
    if (value === 0) return '0';
    if (value < 100) return value.toString();
    // Per valori >= 100, converti in migliaia con un decimale
    const thousands = value / 1000;
    return `${thousands.toFixed(1)}k`;
  };

  // Funzione per formattare i numeri come interi (per il grafico dei giorni)
  const formatIntegerLabel = (value) => {
    if (typeof value !== 'number' || isNaN(value)) return value;
    return value.toString();
  };

  // Funzione per calcolare le statistiche di abbandono
  const calculateAbandonmentStats = (data, rawAnswersArray) => {
    // rawAnswersArray contiene tutti i record prima del parsing
    let totalRawRecords = rawAnswersArray ? rawAnswersArray.length : data.length;
    let validRecords = data.length;
    let corruptedRecords = totalRawRecords - validRecords;
    let incompleteRecords = 0;
    let emptyRecords = 0;
    let profilingOnlyRecords = 0;
    let completeRecords = 0;

    data.forEach(entry => {
      try {
        // Contiamo le sezioni non vuote
        const sections = Object.keys(entry);
        const nonEmptySections = sections.filter(key => {
          const section = entry[key];
          if (Array.isArray(section)) {
            return section.length > 0;
          } else if (typeof section === 'object' && section !== null) {
            return Object.keys(section).length > 0;
          }
          return section !== null && section !== undefined;
        });

        // Classificazione dei record - ora solo JSON vuoti sono considerati abbandoni
        if (nonEmptySections.length === 0) {
          emptyRecords++;
        } else if (nonEmptySections.length === 1 && nonEmptySections.includes('Profiling')) {
          profilingOnlyRecords++;
        } else if (nonEmptySections.length < 6) { // Assumiamo 6+ sezioni per un questionario completo
          incompleteRecords++;
        } else {
          completeRecords++;
        }
      } catch (e) {
        emptyRecords++;
      }
    });

    // Solo JSON vuoti sono considerati abbandoni veri e propri
    const abandonmentRate = ((emptyRecords + profilingOnlyRecords + incompleteRecords) / validRecords * 100);
    const completionRate = (completeRecords / validRecords * 100);

    return {
      totalRawRecords,
      validRecords,
      corruptedRecords,
      completeRecords,
      emptyRecords,
      profilingOnlyRecords,
      incompleteRecords,
      abandonmentRate: abandonmentRate.toFixed(1),
      completionRate: completionRate.toFixed(1)
    };
  };

  useEffect(() => {
    if (isEmpty) return;

    // Calcola le statistiche di abbandono
    // Nota: answerData contiene solo i record parsati con successo dal contesto
    // Per ottenere il numero totale di record dovremmo accedere ai dati raw
    // Per ora assumiamo che il contesto fornisca anche questa informazione
    const stats = calculateAbandonmentStats(answerData);
    setAbandonmentStats(stats);

    fetch("/assets/data/tools_questions.json")
      .then((res) => res.json())
      .then((json) => {
        const profilingSection = json["0"];
        if (!profilingSection || !Array.isArray(profilingSection)) return;

        const counts = profilingSection.map(() => ({}));

        answerData.forEach((entry) => {
          const profiling = entry?.Profiling;
          if (!Array.isArray(profiling)) return;

          profiling.forEach(({ Question, Answer }) => {
            const qIndex = Question - 1;
            const ansKey = String(Answer).trim();
            if (ansKey) {
              counts[qIndex][ansKey] = (counts[qIndex][ansKey] || 0) + 1;
            }
          });
        });

        const preparedCharts = profilingSection.map((question, idx) => {
          const entries = Object.entries(counts[idx] || {});
          const data = entries.map(([key, value]) => {
            const index = parseInt(key);
            const label = question.possible_answers?.[index] || `Answer ${key}`;
            return {
              answer: label,
              count: value,
              truncated: label.length > 15 ? `${label.slice(0, 15)}...` : label,
            };
          });

          const marginBottom = calculateMarginBottom(data);

          return {
            label: question.question,
            data,
            marginBottom,
          };
        });

        if (preparedCharts.length > 0) {
          setChartsData(preparedCharts);
          previousChartsDataRef.current = preparedCharts;
        }
      });
  }, [answerData, isEmpty]);

  // Usa useMemo per i dati dei giorni per evitare re-render non necessari
  const dayOfWeekData = useMemo(() => {
    if (isEmpty || !answerData.length) {
      return previousDayOfWeekDataRef.current.length > 0 ? previousDayOfWeekDataRef.current : [];
    }
    const newData = prepareDayOfWeekData(answerData);
    if (newData.length > 0) {
      previousDayOfWeekDataRef.current = newData;
    }
    return newData.length > 0 ? newData : (previousDayOfWeekDataRef.current.length > 0 ? previousDayOfWeekDataRef.current : []);
  }, [answerData, isEmpty]);

  // Mantieni chartsData stabile durante i re-render
  const stableChartsData = useMemo(() => {
    if (chartsData.length > 0) {
      previousChartsDataRef.current = chartsData;
      return chartsData;
    }
    return previousChartsDataRef.current.length > 0 ? previousChartsDataRef.current : [];
  }, [chartsData]);

  // Calcola i dati filtrati per le ore usando useMemo per evitare flash durante l'aggiornamento
  const filteredDataForHours = useMemo(() => {
    if (isEmpty || !answerData.length) return [];
    if (selectedDay === null) {
      return answerData;
    }
    return answerData.filter(entry => {
      const timestamp = parseTimestamp(entry._timestamp);
      if (!timestamp) return false;
      const dayIndex = timestamp.getDay();
      const dayNames = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
      return dayNames[dayIndex] === selectedDay;
    });
  }, [answerData, selectedDay, isEmpty]);

  // Calcola direttamente i dati delle ore usando useMemo - evita flash durante l'aggiornamento
  // Mantieni i dati precedenti finché i nuovi non sono pronti
  const hourOfDayData = useMemo(() => {
    if (isEmpty || !answerData.length) {
      return previousHourDataRef.current.length > 0 ? previousHourDataRef.current : [];
    }
    if (filteredDataForHours.length === 0) {
      return previousHourDataRef.current.length > 0 ? previousHourDataRef.current : [];
    }
    const newData = prepareHourOfDayData(filteredDataForHours);
    if (newData.length > 0) {
      previousHourDataRef.current = newData;
    }
    return newData.length > 0 ? newData : (previousHourDataRef.current.length > 0 ? previousHourDataRef.current : []);
  }, [filteredDataForHours, isEmpty, answerData]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.name.endsWith(".xlsx")) {
      parseXlsxFile(file);
    } else {
      alert("Please upload a valid .xlsx file");
    }
  };

  return (
    <BaseLayout>
      {isEmpty ? (
        <div className="w-full h-[70vh] flex items-center justify-center">
          <div className="h-[30vh] w-[40vw] flex flex-col items-center justify-center text-center border-2 border-dashed border-gray-300 rounded-3xl p-6 bg-white">
            <p className="text-gray-600 text-base font-medium mb-2">
              Carica qui i tuoi dati
            </p>
            <p className="text-gray-500 text-sm mb-5">
              Trascina un file .xlsx o caricalo dal dispositivo.
            </p>
            <label
              htmlFor="file-upload"
              className="cursor-pointer px-4 py-1.5 rounded-full text-sm font-medium bg-white text-green-600 border border-green-300 hover:bg-green-50 transition"
            >
              Scegli file
            </label>
            <input
              id="file-upload"
              name="sfoglia"
              type="file"
              accept=".xlsx"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        </div>
      ) : (
        <>
          <h2 className="text-xl font-semibold my-6">
            Overview
          </h2>

          {/* Card statistiche abbandono */}
          {abandonmentStats && (
            <div className="bg-white p-6 rounded-lg border border-gray-200 mb-8">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">
                Statistiche Completamento Questionario
                {abandonmentStats && abandonmentStats.corruptedRecords > 0 && (
                  <span className="text-sm font-normal text-gray-500 ml-2">
                    ({abandonmentStats.validRecords} risposte valide su {abandonmentStats.totalRawRecords} totali)
                  </span>
                )}
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-800">
                    {abandonmentStats.totalRawRecords}
                  </div>
                  <div className="text-sm text-gray-600">Record Totali</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600">
                    {abandonmentStats.validRecords}
                  </div>
                  <div className="text-sm text-gray-600">Risposte Valide</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {abandonmentStats.completionRate}%
                  </div>
                  <div className="text-sm text-gray-600">Tasso Completamento</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {abandonmentStats.abandonmentRate}%
                  </div>
                  <div className="text-sm text-gray-600">Tasso Abbandono</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">
                    {abandonmentStats.incompleteRecords + abandonmentStats.profilingOnlyRecords + abandonmentStats.emptyRecords}
                  </div>
                  <div className="text-sm text-gray-600">Questionari Incompleti</div>
                </div>
                {abandonmentStats.corruptedRecords > 0 && (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-600">
                      {abandonmentStats.corruptedRecords}
                    </div>
                    <div className="text-sm text-gray-600">Record Corrotti</div>
                  </div>
                )}
              </div>

              {/* Breakdown dettagliato */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  <div className="flex justify-between items-center mb-1">
                    <span>Questionari completi:</span>
                    <span className="font-medium">{abandonmentStats.completeRecords}</span>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                    <span>Solo sezione demografica:</span>
                    <span className="font-medium">{abandonmentStats.profilingOnlyRecords}</span>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                    <span>Parzialmente completati:</span>
                    <span className="font-medium">{abandonmentStats.incompleteRecords}</span>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                    <span>Record vuoti:</span>
                    <span className="font-medium">{abandonmentStats.emptyRecords}</span>
                  </div>
                  {abandonmentStats.corruptedRecords > 0 && (
                    <div className="flex justify-between items-center">
                      <span>Record corrotti:</span>
                      <span className="font-medium text-gray-500">{abandonmentStats.corruptedRecords}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Grafici giorni e orari di interazione */}
          {(dayOfWeekData.length > 0 || hourOfDayData.length > 0) && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold my-6">
                Distribuzione
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Grafico giorni della settimana */}
                {dayOfWeekData.length > 0 && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h3 className="text-md font-semibold mb-2">
                      Distribuzione per Giorno della Settimana
                      {selectedDay ? (
                        <span className="text-sm font-normal text-gray-500 ml-2">
                          (Filtro: {selectedDay})
                          <button
                            onClick={() => setSelectedDay(null)}
                            className="ml-2 text-red-600 hover:text-red-800 underline"
                          >
                            Rimuovi filtro
                          </button>
                        </span>
                      ) : (
                        <span className="text-sm font-normal text-gray-500 ml-2">
                          (Filtra cliccando su una colonna)
                        </span>
                      )}
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={dayOfWeekData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 0,
                          bottom: 40,
                        }}
                        style={{ outline: 'none' }}
                      >
                        <XAxis
                          dataKey="truncated"
                          interval={0}
                          angle={-20}
                          textAnchor="end"
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis allowDecimals={false} />
                        <Tooltip content={<ChartToolTip />} />
                        <Bar
                          dataKey="count"
                          fill="hsl(200, 70%, 60%)"
                          onClick={(data) => {
                            // Toggle: se clicchi lo stesso giorno, deseleziona
                            if (selectedDay === data.day) {
                              setSelectedDay(null);
                            } else {
                              setSelectedDay(data.day);
                            }
                          }}
                          style={{ cursor: 'pointer', outline: 'none' }}
                        >
                          {dayOfWeekData.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={selectedDay === entry.day
                                ? "hsl(200, 70%, 40%)" // Colore più scuro per il giorno selezionato
                                : "hsl(200, 70%, 60%)"
                              }
                            />
                          ))}
                          <LabelList 
                            dataKey="count" 
                            position="top" 
                            formatter={formatIntegerLabel}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                {/* Grafico ore del giorno */}
                {hourOfDayData.length > 0 && (
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h3 className="text-md font-semibold mb-2">
                      Distribuzione per Ora del Giorno
                      {selectedDay && (
                        <span className="text-sm font-normal text-gray-500 ml-2">
                          (solo {selectedDay})
                        </span>
                      )}
                    </h3>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart
                        data={hourOfDayData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 0,
                          bottom: 60,
                        }}
                        style={{ outline: 'none' }}
                      >
                        <XAxis
                          dataKey="truncated"
                          interval={1}
                          angle={-45}
                          textAnchor="end"
                          tick={{ fontSize: 10 }}
                        />
                        <YAxis allowDecimals={false} />
                        <Tooltip content={<ChartToolTip />} />
                        <Bar
                          dataKey="count"
                          fill="hsl(280, 70%, 60%)"
                        >
                          <LabelList 
                            dataKey="count" 
                            position="top" 
                            formatter={formatCountLabel}
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Grafici esistenti */}
          <h2 className="text-xl font-semibold my-6">
            Profiling
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {stableChartsData.map(({ label, data, marginBottom }, index) => (
              <div key={index} className="bg-white p-4 rounded-lg border border-gray-200">
                <h3 className="text-md font-semibold mb-2">{label}</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={data}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 0,
                      bottom: marginBottom,
                    }}
                    style={{ outline: 'none' }}
                    onMouseDown={(e) => e.preventDefault()}
                  >
                    <XAxis
                      dataKey="truncated"
                      interval={0}
                      angle={-20}
                      textAnchor="end"
                      tick={{ fontSize: 10 }}
                    />
                    <YAxis allowDecimals={false} />
                    <Tooltip content={<ChartToolTip />} />
                    <Bar
                      dataKey="count"
                      fill={`hsl(${(index * 70) % 360}, 70%, 60%)`}
                    >
                      <LabelList dataKey="count" position="top" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ))}
          </div>
        </>
      )}
    </BaseLayout>
  );
}